import { INVENTORY_STATUSES } from "@/api/inventory/_models/Inventory";
import type { InventoryStatus } from "@/api/inventory/_models/Inventory";
import { INVENTORY_LIST_EMPTY_FILTER } from "./InventoryListFilterPanel";
import type { InventoryListFilterValues } from "./InventoryListFilterPanel";

const SEARCH = "search";
const STATUS = "status";
const LOCATION_ID = "locationId";

const FILTER_KEYS = [SEARCH, STATUS, LOCATION_ID];

/** The filter panel's open state in the URL: ?filter=open. */
export const INVENTORY_LIST_FILTER_PANEL_PARAM = "filter";
const FILTER_PANEL_OPEN = "open";

const isInventoryStatus = (value: string): value is InventoryStatus => {
  return INVENTORY_STATUSES.some((status) => status === value);
};

/**
 * The inventory list's filters from the URL (e.g. ?status=differs&locationId=…&search=DRE-L50).
 * An unknown status is dropped.
 */
export const toInventoryListFilterFromSearchParams = (params: URLSearchParams): InventoryListFilterValues => {
  return {
    ...INVENTORY_LIST_EMPTY_FILTER,
    search: params.get(SEARCH) ?? "",
    statuses: params.getAll(STATUS).filter((status) => isInventoryStatus(status)),
    locationIds: params.getAll(LOCATION_ID),
  };
};

/** Whether the URL says the filter panel is open. */
export const isInventoryListFilterPanelOpenInSearchParams = (params: URLSearchParams): boolean => {
  return params.get(INVENTORY_LIST_FILTER_PANEL_PARAM) === FILTER_PANEL_OPEN;
};

/**
 * Writes the filter and the panel state into `params`, replacing what was there and leaving
 * every other param alone. Lists repeat the key; empty values are left out.
 */
export const writeInventoryListFilterToSearchParams = (
  params: URLSearchParams,
  filter: InventoryListFilterValues,
  isPanelOpen: boolean,
): void => {
  for (const key of [...FILTER_KEYS, INVENTORY_LIST_FILTER_PANEL_PARAM]) {
    params.delete(key);
  }
  if (isPanelOpen) params.set(INVENTORY_LIST_FILTER_PANEL_PARAM, FILTER_PANEL_OPEN);
  const search = filter.search.trim();
  if (search) params.set(SEARCH, search);
  for (const status of filter.statuses) params.append(STATUS, status);
  for (const locationId of filter.locationIds) params.append(LOCATION_ID, locationId);
};
